import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { type Customer, useCustomers } from "@/hooks/use-customers";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface CustomerSelectProps {
  value: string | null;
  onChange: (customerId: string, customer: Customer) => void;
  /** Label to show on the trigger for a preselected value not yet in the results. */
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Server-side-searchable single-select for customers. */
export function CustomerSelect({
  value,
  onChange,
  selectedLabel,
  disabled,
  className,
}: CustomerSelectProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useCustomers(
    { search: debouncedSearch, status: "" },
    { cursor: null, direction: "next" },
  );
  const customers = data?.data ?? [];

  return (
    <AsyncCombobox
      value={value}
      onChange={onChange}
      options={customers}
      getOptionValue={(customer) => customer.id}
      getOptionText={(customer) => customer.name}
      getOptionLabel={(customer) => (
        <span className="flex flex-col">
          <span>{customer.name}</span>
          <span className="text-xs text-muted-foreground">{customer.email}</span>
        </span>
      )}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={isFetching}
      disabled={disabled}
      className={className}
      placeholder="Select a customer…"
      searchPlaceholder="Search customers…"
      emptyText="No customers found."
    />
  );
}
